import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDate } from '../lib/format'

interface PracticeMe {
  home_org_id: string;
}
interface BillingState {
  state: "none" | "trial" | "overdue" | "stopped";
  date: string | null;
}

/** True when the signed-in user is acting on a practice client (not the practice's own org). */
export function usePracticeSwitched(): boolean {
  const { user } = useAuth();
  const me = useQuery({
    queryKey: ["practice-me"],
    queryFn: () => api.get<PracticeMe>("/api/v1/practice/me"),
    retry: false,
  });
  return !!me.data && !!user && user.org_id !== me.data.home_org_id;
}

function fmt(iso: string) {
  return formatDate(iso, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Shown instead of the client's own billing notice while a practice acts on
 * a client: clients inherit the practice's billing. Read-only; the owner
 * pays from Settings in the practice's own organisation. No price is shown.
 */
export function PracticeBillingBanner() {
  const switched = usePracticeSwitched();
  const q = useQuery({
    queryKey: ["practice-billing-status"],
    enabled: switched,
    queryFn: () => api.get<BillingState>("/api/v1/practice/billing-status"),
  });
  if (!switched || !q.data || q.data.state === "none") return null;
  const { state, date } = q.data;
  const text =
    state === "trial"
      ? `Your practice trial ends on ${date ? fmt(date) : "soon"}. Add a payment method to keep sending for your clients.`
      : state === "overdue"
        ? `Payment is overdue. Sending for your clients stops on ${date ? fmt(date) : "soon"} unless this is fixed.`
        : "Sending for your clients has stopped because payment is overdue.";
  return (
    <div
      data-testid="practice-billing-banner"
      className={
        "rounded-lg border px-4 py-3 text-sm " +
        (state === "stopped"
          ? "border-danger-200 bg-danger-50 text-danger-700"
          : "border-warning-200 bg-warning-50 text-warning-700")
      }
    >
      {text}
    </div>
  );
}
